import React from "react";
import {
  SearchQueryFunnelResponse,
  SearchQuerySort,
} from "../../api/analyticsAdmin";
import { formatRate, queryLabel } from "../../helpers/searchAnalytics";
import { formatInteger } from "../../helpers/formatNumber";
import SectionBody from "./SectionBody";
import { SectionState } from "./useAnalyticsStream";
import { sortUsesMinSearches } from "./searchSections";

const SORTS: {
  key: SearchQuerySort;
  label: string;
  description: string;
}[] = [
  {
    key: "Volume",
    label: "Highest volume",
    description: "Most-searched queries first — what people look for most.",
  },
  {
    key: "OpenRate",
    label: "Highest open rate",
    description:
      "Queries most likely to lead to a business open (share of searches with an open).",
  },
  {
    key: "OutboundRate",
    label: "Highest outbound rate",
    description:
      "Most convertible queries — searches that most often end in an outbound click.",
  },
  {
    key: "ZeroConversion",
    label: "Lowest outbound rate",
    description:
      "Weakest outbound conversion first — surfaces high-volume, under-monetized demand. A ranking, not a filter, so queries may still have a few clicks.",
  },
  {
    key: "LowestOpenRate",
    label: "Lowest open rate",
    description:
      "Weakest open rate first — high-volume queries whose results rarely lead to an open.",
  },
  {
    key: "ZeroResults",
    label: "Zero results",
    description:
      "Only queries that always return zero results, highest-volume first — catalog gaps worth filling.",
  },
];

const MIN_SEARCHES_HINT =
  "Hides queries with fewer than this many searches, so noisy rates from tiny samples don't dominate the ranking.";

// Both controls change the request — this endpoint still sorts and cuts server
// side — so they live on the page that assembles the batch. Only this section is
// re-requested when either moves.
const SearchQueryFunnel: React.FC<{
  section: SectionState<SearchQueryFunnelResponse>;
  sort: SearchQuerySort;
  onSortChange: (sort: SearchQuerySort) => void;
  minSearches: number;
  onMinSearchesChange: (minSearches: number) => void;
}> = ({ section, sort, onSortChange, minSearches, onMinSearchesChange }) => {

  const activeSort = SORTS.find((s) => s.key === sort) ?? SORTS[0];

  // The ZeroResults sort filters server-side, so an empty result is a
  // meaningful "none found" rather than "no data for the range".
  const emptyMessage =
    sort === "ZeroResults"
      ? "No always-empty queries above the min-searches threshold. 🎉"
      : "No queries for this range.";

  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h3 className="text-lg font-medium">Top queries</h3>
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600">
            Sort{" "}
            <select
              value={sort}
              onChange={(e) => onSortChange(e.target.value as SearchQuerySort)}
              className="p-2 border border-gray-300 rounded-lg text-sm ml-1"
            >
              {SORTS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          {sortUsesMinSearches(sort) && (
            <label
              className="text-sm text-gray-600"
              title={MIN_SEARCHES_HINT}
            >
              Min searches{" "}
              <input
                type="number"
                min={0}
                value={minSearches}
                onChange={(e) =>
                  onMinSearchesChange(Math.max(0, Number(e.target.value) || 0))
                }
                title={MIN_SEARCHES_HINT}
                className="w-20 p-2 border border-gray-300 rounded-lg text-sm ml-1"
              />
            </label>
          )}
        </div>
      </div>

      <p className="text-xs text-gray-400 mb-4">
        {activeSort.description}
        {sortUsesMinSearches(sort) &&
          " Queries below the min-searches threshold are hidden to reduce noise."}
      </p>

      <SectionBody
        section={section}
        isEmpty={(data) => data.items.length === 0}
        emptyMessage={emptyMessage}
      >
        {(data) => (
          <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="text-left font-medium px-4 py-3">Query</th>
                <th className="text-right font-medium px-4 py-3">Searches</th>
                <th className="text-right font-medium px-4 py-3">Searchers</th>
                <th className="text-right font-medium px-4 py-3">Opens</th>
                <th className="text-right font-medium px-4 py-3">Outbound</th>
                <th className="text-right font-medium px-4 py-3">S→O</th>
                <th className="text-right font-medium px-4 py-3">O→Out</th>
                <th className="text-right font-medium px-4 py-3">S→Out</th>
                <th className="text-right font-medium px-4 py-3">Zero results</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <tr key={row.query} className="border-t border-gray-100">
                  <td className="px-4 py-3">{queryLabel(row)}</td>
                  <td className="px-4 py-3 text-right">
                    {formatInteger(row.searches)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {formatInteger(row.uniqueSearchers)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {formatInteger(row.opens)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {formatInteger(row.outbounds)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {formatRate(row.searchToOpenRate)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {formatRate(row.openToOutboundRate)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {formatRate(row.searchToOutboundRate)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {formatRate(row.zeroResultRate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </SectionBody>
    </section>
  );
};

export default SearchQueryFunnel;
